import { useTranslation } from 'react-i18next';
import { MIN_COL_WIDTH } from './fieldsEditorShared';

/** Drag handle that reports a live width during drag and commits on release. */
export function ColumnResizer({
  width,
  onLive,
  onCommit,
}: {
  width: number;
  onLive: (w: number) => void;
  onCommit: (w: number) => void;
}) {
  const { t } = useTranslation('knowledge');
  /**
   * Pointer CAPTURE on the handle, with the listeners on the handle itself.
   *
   * They used to go on `window`, removed only by the `pointerup` handler — so
   * unmounting mid-drag (switching version, closing the tab) left them attached,
   * and the next stray `pointerup` anywhere on the page committed a width
   * through a stale closure over a column that was gone.
   *
   * Capture routes every subsequent pointer event to this element even when the
   * pointer leaves it, which is what a drag needs, and the browser releases it
   * automatically when the element is removed — so there is nothing left to leak.
   */
  const handlePointerDown = (e: React.PointerEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const handle = e.currentTarget;
    const startX = e.clientX;
    const startWidth = width;
    const clamp = (clientX: number) => Math.max(MIN_COL_WIDTH, startWidth + (clientX - startX));
    const move = (ev: PointerEvent) => onLive(clamp(ev.clientX));
    const up = (ev: PointerEvent) => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', up);
      handle.removeEventListener('pointercancel', up);
      onCommit(clamp(ev.clientX));
    };
    handle.setPointerCapture(e.pointerId);
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
    // Capture can be lost without a pointerup (a browser gesture takes over),
    // which would otherwise leave the drag armed forever.
    handle.addEventListener('pointercancel', up);
  };

  return (
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label={t('taxonomy.fieldsEditor.resizeColumn')}
      onPointerDown={handlePointerDown}
      className="absolute -right-1.5 top-0 z-20 h-full w-3 cursor-col-resize touch-none select-none bg-transparent hover:bg-primary/40 active:bg-primary/60"
    />
  );
}
